import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import type { OperationsQueue } from '@/types/stats.types';

interface OperationsQueueCardProps {
  queue: OperationsQueue;
  onOpenPayments: () => void;
  onOpenReports: () => void;
}

/**
 * Replaces the prototype's "Platform Health" card, keeping its 4-cell `.health-grid` geometry.
 *
 * The prototype measured uptime, API latency, fraud rate and notification delivery — none of
 * which Shoutt instruments, so showing them would mean showing invented numbers. What an admin
 * actually needs on the landing page is what is waiting for them, which this does show.
 */
const OperationsQueueCard: FC<OperationsQueueCardProps> = ({
  queue,
  onOpenPayments,
  onOpenReports,
}) => {
  const cells = [
    { icon: '💳', label: 'Awaiting Verification', value: queue.awaitingPaymentVerification, onClick: onOpenPayments },
    { icon: '💸', label: 'Pending Settlements', value: queue.pendingSettlements, onClick: onOpenPayments },
    { icon: '⚖️', label: 'Open Disputes', value: queue.openDisputes, onClick: onOpenPayments },
    { icon: '🛡️', label: 'Moderation Queue', value: queue.moderationQueue, onClick: onOpenReports },
  ];
  const total = cells.reduce((sum, cell) => sum + cell.value, 0);

  return (
    <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-6 shadow-[var(--shadow-card)]">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-extrabold text-[var(--color-ink)]">Operations Queue</h2>
        <Pill tone={total === 0 ? 'success' : 'warn'}>
          {total === 0 ? 'Nothing waiting ✓' : `${total} waiting`}
        </Pill>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cells.map((cell) => (
          <button
            key={cell.label}
            type="button"
            onClick={cell.onClick}
            className="rounded-[10px] bg-[var(--color-surface-muted)] px-3 py-4 text-center transition-colors hover:bg-[var(--color-surface-muted-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <div className="mb-1.5 text-2xl">{cell.icon}</div>
            <div
              className="text-[15px] font-extrabold"
              style={{
                color: cell.value > 0 ? 'var(--color-warn)' : 'var(--color-success)',
              }}
            >
              {cell.value}
            </div>
            <div className="text-[11px] text-[var(--color-ink-muted)]">{cell.label}</div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default OperationsQueueCard;
